import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase }                                  from '@/shared/lib/supabase';
import type { FutebolVotacaoJogo }                   from '@/shared/types';
import { votacaoRepository }                          from '../../data/votacaoRepository';
import { atualizarIdsUltimoCiclo } from '../../domain/usecases/selecionarCandidatos';
import { derivarCandidatos, ordenarPorVotos } from '../../domain/usecases/derivarCandidatos';

export interface TvVotacaoState {
  jogos:      FutebolVotacaoJogo[];
  loading:    boolean;
  totalVotos: number;
  lider:      FutebolVotacaoJogo | null;
  vencedor:   FutebolVotacaoJogo | null;
}

export function useTvVotacao(telaId: string): TvVotacaoState {
  const [jogos,   setJogos]   = useState<FutebolVotacaoJogo[]>([]);
  const [loading, setLoading] = useState(true);

  const carregarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = telaId ? await votacaoRepository.buscarVotacaoHoje(telaId) : [];
      setJogos(data);
    } finally {
      setLoading(false);
    }
  }, [telaId]);

  const cbRef = useRef({ setJogos, carregar });
  useEffect(() => { cbRef.current = { setJogos, carregar }; });

  useEffect(() => {
    if (!telaId) {
      setJogos([]);
      setLoading(false);
      return;
    }
    carregar();

    const channel = supabase
      .channel(`futebol-votacao-tv-${telaId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'futebol_votacao_jogos', filter: `tela_id=eq.${telaId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            const updated = payload.new as FutebolVotacaoJogo;
            cbRef.current.setJogos((prev) =>
              ordenarPorVotos(prev.map((j) => (j.id === updated.id ? updated : j))),
            );
          } else {
            // Debounce: fim de ciclo dispara vários DELETE seguidos — agrupa num único re-fetch
            if (carregarTimerRef.current) clearTimeout(carregarTimerRef.current);
            carregarTimerRef.current = setTimeout(() => {
              cbRef.current.carregar();
            }, 300);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (carregarTimerRef.current) clearTimeout(carregarTimerRef.current);
    };
  }, [carregar]);

  // Memoriza o conjunto de fixture_ids do último ciclo com candidatos ativos.
  const ultimoCicloIdsRef = useRef<number[]>([]);
  const { ativos, totalVotos, lider, vencedor: vencedorCandidato } =
    derivarCandidatos(jogos, ultimoCicloIdsRef.current);
  useEffect(() => {
    ultimoCicloIdsRef.current = atualizarIdsUltimoCiclo(ultimoCicloIdsRef.current, ativos.map((j) => j.fixture_id));
  }, [ativos]);

  // Anuncia o vencedor só quando não há votação ativa em andamento
  const vencedor = ativos.length === 0 ? vencedorCandidato : null;

  return { jogos: ativos, loading, totalVotos, lider, vencedor };
}
